import { getProposalInvoicingStatus } from '../../../utils/proposalInvoicing';

export function capitalize(s) {
  return String(s).charAt(0).toUpperCase() + String(s).slice(1).replace(/_/g, ' ');
}

export function proposalBadgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'approved') return 'cp-badge-green';
  if (s === 'sent') return 'cp-badge-blue';
  if (s === 'declined') return 'cp-badge-red';
  if (s === 'changes_requested') return 'cp-badge-yellow';
  return 'cp-badge-gray';
}

export function invoiceBadgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'paid') return 'cp-badge-green';
  if (s === 'partial') return 'cp-badge-yellow';
  if (s === 'unpaid') return 'cp-badge-red';
  return 'cp-badge-gray';
}

export const PO_STATUS_ORDER = [
  'No status',
  'Ordered',
  'Back Ordered',
  'Workroom',
  'En route',
  'Delivered',
  'Installed',
];

/* the PO editor (orderStatuses.js BUILT_IN_PO_STATUSES) also allows 'Draft' and
   'Cancelled', which aren't points on the fulfillment track — map them onto the
   nearest track rank so the client-facing progress bar still renders instead of
   going blank. An account's custom statuses aren't on the track either: they
   show as a neutral badge with no step filled, since only the studio knows
   where a status like "At Workroom" sits in the client-facing sequence. */
const STATUS_RANK_ALIASES = { draft: 'No status' };

function rankedStatus(status) {
  const alias = STATUS_RANK_ALIASES[String(status || '').toLowerCase()];
  return alias || status;
}

export function poBadgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'delivered' || s === 'installed' || s === 'paid') return 'cp-badge-green';
  if (s === 'ordered' || s === 'en route' || s === 'workroom') return 'cp-badge-blue';
  if (s === 'back ordered') return 'cp-badge-yellow';
  if (s === 'cancelled') return 'cp-badge-red';
  return 'cp-badge-gray';
}

export function isStepReached(currentStatus, step) {
  const currentIdx = PO_STATUS_ORDER.indexOf(rankedStatus(currentStatus));
  const stepIdx = PO_STATUS_ORDER.indexOf(step);
  if (currentIdx === -1 || stepIdx === -1) return false;
  return stepIdx <= currentIdx;
}

export function formatDate(d) {
  if (!d) return '';
  const date = new Date(d);
  return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}

export function formatNumber(n) {
  return Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatMoney(n) {
  return `$${formatNumber(n)}`;
}

export function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/* a human-readable row title built from line items — proposals/invoices don't
   carry their own title, and projectName is identical across every row for a
   project, so it can't tell rows apart */
export function docTitle(lineItems, fallbackLabel) {
  const items = Array.isArray(lineItems) ? lineItems : [];
  const firstName = items[0]?.clientName || items[0]?.name || items[0]?.description || '';
  if (!firstName) return fallbackLabel;
  return items.length > 1 ? `${firstName} + ${items.length - 1} more` : firstName;
}

/* remaining deposit owed on a proposal (0 once the deposit has been collected,
   or once every item is invoiced — it's collected on the invoice from then on) */
export function proposalDepositBalance(p) {
  if (getProposalInvoicingStatus(p) === 'invoiced') return 0;
  return Math.max(round2((p.totals?.depositDue || 0) - (p.totals?.paid || 0)), 0);
}

/* status/badge shown on a proposal row — approximates the client-facing lifecycle
   (sent -> ready for approval, approved -> invoiced/converted to procurement).
   A proposal can also be invoiced a few items at a time; once every item is,
   that's what the row shows, while a pending action on the items still on it
   outranks "Partially Invoiced". */
export function proposalDisplayStatus(p, canApprove) {
  const invoicing = getProposalInvoicingStatus(p);
  if (invoicing === 'invoiced') return { label: 'Invoiced', cls: 'cp-badge-green' };
  if (p.status === 'declined') return { label: 'Declined', cls: 'cp-badge-red' };
  if (p.status === 'changes_requested')
    return { label: 'Changes Requested', cls: 'cp-badge-yellow' };
  if (p.status === 'sent') {
    return canApprove
      ? { label: 'Ready For Approval', cls: 'cp-badge-blue' }
      : { label: 'Sent', cls: 'cp-badge-blue' };
  }
  if (invoicing === 'partially_invoiced') return { label: 'Partially Invoiced', cls: 'cp-badge-yellow' };
  if (p.status === 'approved') return { label: 'Invoiced', cls: 'cp-badge-green' };
  return { label: capitalize(p.status || 'draft'), cls: 'cp-badge-gray' };
}

/* groups a flat list of items by their `room` field, "Unspecified" last */
export function groupByRoom(items, roomFilter) {
  const filtered = roomFilter
    ? items.filter((it) => (it.room || 'Unspecified') === roomFilter)
    : items;
  const groups = new Map();
  for (const item of filtered) {
    const room = item.room || 'Unspecified';
    if (!groups.has(room)) groups.set(room, []);
    groups.get(room).push(item);
  }
  return Array.from(groups.entries()).sort(([a], [b]) => {
    if (a === 'Unspecified') return 1;
    if (b === 'Unspecified') return -1;
    return a.localeCompare(b);
  });
}

/* where an invoice's "pay/view" link should go — the client's own shareable
   public link when one exists, otherwise the in-portal invoice page */
export function invoiceHref(inv, projectId) {
  return inv.shareToken
    ? { href: `/public/invoice/${inv.shareToken}`, isExternal: true }
    : { href: `/client/projects/${projectId}/invoices/${inv.invoiceId}`, isExternal: false };
}

export function uniqueRooms(items) {
  return Array.from(new Set(items.map((it) => it.room || 'Unspecified'))).sort((a, b) => {
    if (a === 'Unspecified') return 1;
    if (b === 'Unspecified') return -1;
    return a.localeCompare(b);
  });
}
